import type { CountyAdapter, ParsedDocument } from "../types";

const reverseName = (name: string): string => {
  const commaCount = (name.match(/,/g) || []).length;
  if (commaCount === 1) {
    const [last, first] = name.split(",").map((s) => s.trim());
    if (first && last) return `${first} ${last}`;
  }
  return name;
};

const parseDate = (s: string): string => {
  const [m, d, y] = s.trim().split("/");
  return y ? `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}` : s;
};

interface Working {
  filingDate?: string;
  documentNumber?: string;
  documentType?: string;
  seller?: string;
  buyer?: string;
  legal?: string;
  township?: string;
  parcel?: string;
  hasReferral?: boolean;
}

const parse = (text: string): ParsedDocument[] => {
  const lines = text.split("\n");
  const out: ParsedDocument[] = [];
  let cur: Working = {};
  const flush = () => {
    if (cur.documentNumber && cur.seller && cur.buyer) {
      out.push({
        documentNumber: cur.documentNumber,
        documentType: cur.documentType || "LAND CONTRACT",
        filingDate: cur.filingDate!,
        grantors: [cur.seller],
        grantees: [cur.buyer],
        legalDescription: cur.legal,
        propertyAddress: cur.township,
        notes: cur.parcel ? `Parcel: ${cur.parcel}` : undefined,
        skipReason: cur.hasReferral ? "Additional referrals" : undefined,
      });
    }
    cur = {};
  };

  for (const raw of lines) {
    const line = raw.trim();
    const header = line.match(
      /^(\d{1,2}\/\d{1,2}\/\d{4})\s+(\d{5}-\d{5})\s+(.+?)\s*$/i,
    );
    if (header && header[3].toUpperCase().includes("LAND CONTRACT")) {
      flush();
      cur = {
        filingDate: parseDate(header[1]),
        documentNumber: header[2],
        documentType: header[3].trim().toUpperCase(),
      };
      continue;
    }
    const sm = line.match(/^[X\s]*SELLER\s+(.+)$/i);
    if (sm && cur.documentNumber && !cur.seller) {
      cur.seller = reverseName(sm[1].trim());
      continue;
    }
    const bm = line.match(/^[X\s]*BUYER\s+(.+)$/i);
    if (bm && cur.documentNumber && !cur.buyer) {
      cur.buyer = reverseName(bm[1].trim());
      continue;
    }
    const sec = line.match(
      /^(SEC\s+\d+\s+T\d+N\s+R\d+W\s+.+?)\s{2,}(.+TOWNSHIP|CITY OF .+)/i,
    );
    if (sec && cur.documentNumber && !cur.legal) {
      cur.legal = sec[1].trim();
      cur.township = sec[2].trim();
      continue;
    }
    const lot = line.match(
      /^(LOTS?\s+[\d,-]+(?:,?\s*BLOCK\s*\d+)?)\s{2,}(.+TOWNSHIP|CITY OF .+)/i,
    );
    if (lot && cur.documentNumber && !cur.legal) {
      cur.legal = lot[1].trim();
      cur.township = lot[2].trim();
      continue;
    }
    if (line.includes(">>>>>> ADDITIONAL")) {
      if (line.toUpperCase().includes("REFERRAL")) cur.hasReferral = true;
      const pm = line.match(/(\d{2}-\d{2}-\d{2}-\d{3}-\d{3})$/);
      if (pm) cur.parcel = pm[1];
      continue;
    }
    const pOnly = line.match(/^(\d{2}-\d{2}-\d{2}-\d{3}-\d{3})$/);
    if (pOnly && cur.documentNumber && !cur.parcel) cur.parcel = pOnly[1];
  }
  flush();
  return out;
};

export const newaygoAdapter: CountyAdapter = {
  countyId: "newaygo",
  countyLabel: "Newaygo County, MI - Land Contract Parser",
  description:
    "Paste land contract search results from Newaygo County. Auto-removes duplicates, filters ADDITIONAL REFERRAL records, and excludes family/bank/business transfers.",
  placeholder: `Paste Newaygo County land contract search results here...

Example:
   12/30/2021    00487-00013                     LAND CONTRACT
   SELLER           DUNN TRUST, SUSAN M, TRUSTEE
 X BUYER            DUNN, JOSHUA
   SEC 04 T11N R11W THE SW OF THE SW QTR           ENSLEY TOWNSHIP
>>>>>> ADDITIONAL PARTIES AND REFERRALS               62-24-04-300-003`,
  detectionReasons: ["Land Contract document type", "Newaygo County MI"],
  confidenceScore: 80,
  filterRules: [
    "SELLER = Grantor (the person we want to contact)",
    "BUYER = Grantee",
    "Records flagged with ADDITIONAL REFERRALS are skipped",
    "Family/bank/business filters applied",
  ],
  parse,
};
